import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { subscriptionRebuildAPI, subscriptionVendorAPI, vendorAliasLearningAPI } from '../../lib/api/subscriptions';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

const STAT_LABELS = {
  created: 'Created',
  converted_from_detected: 'Converted from bank-only',
  updated: 'Updated',
  superseded: 'Superseded',
  no_invoices: 'No invoices (skipped)',
  detector_only_labeled: 'Detected-from-bank-only',
  entity_stamped: 'Entity stamped (was unassigned)',
  cross_entity_candidates: 'Cross-entity candidates (intercompany, not matched)',
};

// Rebuild Subscriptions From Invoices (Prompt Y): runs services/
// subscription_vendors.py::rebuild_vendor_subscription over every
// recognized vendor x every entity, seeding the observed vendor set,
// merging/superseding stale bank-pattern-detected duplicates, and
// building each subscription's monthly invoice/payment history.
export function SubscriptionRebuildPanel() {
  const [preview, setPreview] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);

  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  const previewPoll = useJobPolling({
    fetchStatus: subscriptionRebuildAPI.jobStatus,
    onCompleted: useCallback((result) => {
      setPreview(result);
      setPreviewFailed(null);
      setPreviewProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
      setPreviewProgress(null);
    }, []),
    onProgress: setPreviewProgress,
  });

  const applyPoll = useJobPolling({
    fetchStatus: subscriptionRebuildAPI.jobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
    }, []),
    onProgress: setApplyProgress,
  });

  useJobResume({ fetchLatest: useCallback(() => subscriptionRebuildAPI.jobLatest(true), []), poll: previewPoll });
  useJobResume({ fetchLatest: useCallback(() => subscriptionRebuildAPI.jobLatest(false), []), poll: applyPoll });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setPreviewProgress(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await subscriptionRebuildAPI.start(true);
      if (data.job_id) previewPoll.start(data.job_id);
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setPreviewStarting(false);
    }
  };

  const previewBusy = previewStarting || previewPoll.polling;

  const runApply = async () => {
    const changeCount = (preview?.created || 0) + (preview?.converted_from_detected || 0) + (preview?.updated || 0);
    if (!window.confirm(
      `Rebuild ${changeCount} subscription(s) from the invoice corpus now? A stale bank-detected ` +
      'entry for a recognized vendor is replaced in place (its history is kept, never duplicated); ' +
      'a bank-detected-only vendor with no invoices is left untouched.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const { data } = await subscriptionRebuildAPI.start(false);
      if (data.job_id) applyPoll.start(data.job_id);
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setApplyStarting(false);
    }
  };

  const applyBusy = applyStarting || applyPoll.polling;

  const renderStats = (result, testIdPrefix) => (
    <div className="flex flex-wrap gap-2">
      {Object.keys(STAT_LABELS).map((key) => (
        <Badge key={key} variant="outline" className="text-[10px]" data-testid={`${testIdPrefix}-stat-${key}`}>
          {STAT_LABELS[key]}: {result[key] ?? 0}
        </Badge>
      ))}
    </div>
  );

  return (
    <Card data-testid="subscription-rebuild-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Rebuild Subscriptions From Invoices</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Seeds the recognized-vendor list with the observed set, then rebuilds every recognized
          vendor's subscription from its actual invoice stream (cadence, amount, and monthly
          history all derived from invoices, not bank-pattern guesses). A stale bank-detected
          duplicate for the same vendor is replaced in place, carrying its history forward -- never
          duplicated. A vendor detected from the bank feed only, with no matching invoices, is left
          untouched. Runs in the background; safe to navigate away and come back.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={runPreview} disabled={previewBusy}
            data-testid="subscription-rebuild-preview"
          >
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && (
            <Button
              variant="default" size="sm" onClick={runApply} disabled={applyBusy}
              data-testid="subscription-rebuild-apply"
            >
              {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply
            </Button>
          )}
        </div>

        {previewPoll.polling && (
          <JobProgressLine progress={previewProgress} testId="subscription-rebuild-preview-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} vendor/entity pairs</>}
          </JobProgressLine>
        )}
        <JobErrorBlock error={previewFailed} testId="subscription-rebuild-preview-error" />
        {preview && (
          <div className="space-y-2" data-testid="subscription-rebuild-preview-result">
            {renderStats(preview, 'subscription-rebuild-preview')}
          </div>
        )}

        {applyPoll.polling && (
          <JobProgressLine progress={applyProgress} testId="subscription-rebuild-apply-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} vendor/entity pairs</>}
          </JobProgressLine>
        )}
        <JobErrorBlock error={applyFailed} testId="subscription-rebuild-apply-error" />
        {applyResult && !applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900 space-y-2"
            data-testid="subscription-rebuild-apply-result"
          >
            {renderStats(applyResult, 'subscription-rebuild-apply')}
          </div>
        )}

        <FieldSamplePanel />
        <LearnedAliasesPanel />
      </CardContent>
    </Card>
  );
}

// Confirm-driven alias learning (Prompt: vendor evidence upgrade, Part B):
// every HUMAN confirm teaches a tx-supplier-text -> vendor pairing into the
// recognized-vendor registry above (services/vendor_alias_learning.py), so
// the operator never has to hand-maintain aliases for a recurring vendor
// after one confirm. This section surfaces what's been learned so far
// (provenance-badged, per-row removable), any collisions the learner
// refused to auto-resolve, and a one-click backfill over past confirms.
function LearnedAliasesPanel() {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillError, setBackfillError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [vendorsResp, conflictsResp] = await Promise.all([
        subscriptionVendorAPI.list(),
        vendorAliasLearningAPI.listConflicts(),
      ]);
      setVendors(vendorsResp.data?.vendors ?? []);
      setConflicts(conflictsResp.data?.conflicts ?? []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = () => {
    setOpen(true);
    if (vendors.length === 0 && conflicts.length === 0) load();
  };

  const removeLearned = async (vendorId, normalizedAlias) => {
    setRemoving(`${vendorId}:${normalizedAlias}`);
    setError(null);
    try {
      await vendorAliasLearningAPI.removeLearnedAlias(vendorId, normalizedAlias);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRemoving(null);
    }
  };

  const dismissConflict = async (conflict) => {
    const key = `${conflict.normalized_alias}:${conflict.attempted_vendor_id}:${conflict.existing_vendor_id}`;
    setDismissing(key);
    setError(null);
    try {
      await vendorAliasLearningAPI.dismissConflict(
        conflict.normalized_alias, conflict.attempted_vendor_id, conflict.existing_vendor_id,
      );
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDismissing(null);
    }
  };

  const runBackfillPreview = async () => {
    setBackfillBusy(true);
    setBackfillError(null);
    try {
      const { data } = await vendorAliasLearningAPI.backfill(true);
      setBackfillPreview(data);
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillBusy(false);
    }
  };

  const runBackfillApply = async () => {
    const count = backfillPreview?.outcome_counts?.would_learn ?? 0;
    if (!window.confirm(
      `Apply confirm-driven alias learning to ${count} pairing(s) from existing confirmed matches now?`
    )) return;
    setBackfillBusy(true);
    setBackfillError(null);
    try {
      const { data } = await vendorAliasLearningAPI.backfill(false);
      setBackfillPreview(data);
      await load();
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillBusy(false);
    }
  };

  const vendorsWithLearned = vendors.filter((v) => (v.learned_aliases ?? []).length > 0);

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={handleOpen} data-testid="learned-aliases-open">
        Learned from confirms
      </Button>
    );
  }

  return (
    <div className="border rounded p-3 space-y-3 text-xs" data-testid="learned-aliases-panel">
      <p className="text-muted-foreground">
        Every human confirm of a suggested match teaches the vendor↔transaction-text pairing here
        automatically -- manual alias entry above stays possible but is never required for a
        recurring vendor after one confirm.
      </p>

      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {error && <p className="text-red-600">{error}</p>}

      <div className="space-y-1" data-testid="learned-aliases-list">
        <p className="font-medium">Learned aliases</p>
        {vendorsWithLearned.length === 0 && !loading ? (
          <p className="text-muted-foreground">None learned yet.</p>
        ) : (
          vendorsWithLearned.map((vendor) => (
            <div key={vendor.id} className="border-t pt-1" data-testid={`learned-aliases-vendor-${vendor.id}`}>
              <span className="font-medium">{vendor.vendor_name}</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(vendor.learned_aliases ?? []).map((entry) => (
                  <Badge
                    key={entry.normalized_alias} variant="secondary" className="text-[10px] gap-1"
                    data-testid={`learned-alias-${vendor.id}-${entry.normalized_alias}`}
                  >
                    {entry.alias}
                    <span className="text-muted-foreground">(learned from confirm)</span>
                    <button
                      type="button"
                      className="ml-1 text-muted-foreground hover:text-red-600"
                      onClick={() => removeLearned(vendor.id, entry.normalized_alias)}
                      disabled={removing === `${vendor.id}:${entry.normalized_alias}`}
                      data-testid={`learned-alias-remove-${vendor.id}-${entry.normalized_alias}`}
                    >
                      x
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="space-y-1" data-testid="learned-aliases-conflicts">
        <p className="font-medium">Conflicts (never auto-resolved)</p>
        {conflicts.length === 0 ? (
          <p className="text-muted-foreground">None.</p>
        ) : (
          conflicts.map((conflict) => {
            const key = `${conflict.normalized_alias}:${conflict.attempted_vendor_id}:${conflict.existing_vendor_id}`;
            return (
              <div
                key={key}
                className="flex items-center justify-between gap-2 border-t pt-1"
                data-testid={`learned-alias-conflict-${key}`}
              >
                <span>
                  "{conflict.alias}" -- {conflict.attempted_vendor_name} vs already-owned by{' '}
                  {conflict.existing_vendor_name}
                </span>
                <Button
                  variant="outline" size="sm" className="h-6 text-[10px]"
                  onClick={() => dismissConflict(conflict)}
                  disabled={dismissing === key}
                  data-testid={`learned-alias-conflict-dismiss-${key}`}
                >
                  {dismissing === key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Dismiss'}
                </Button>
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-2 border-t pt-2">
        <p className="font-medium">Backfill from existing confirms</p>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={runBackfillPreview} disabled={backfillBusy}
            data-testid="learned-alias-backfill-preview"
          >
            {backfillBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {backfillPreview && !backfillPreview.error && (
            <Button
              variant="default" size="sm" onClick={runBackfillApply} disabled={backfillBusy}
              data-testid="learned-alias-backfill-apply"
            >
              {backfillBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply
            </Button>
          )}
        </div>
        {backfillError && <p className="text-red-600">{backfillError}</p>}
        {backfillPreview && (
          <div className="flex flex-wrap gap-2" data-testid="learned-alias-backfill-result">
            {Object.entries(backfillPreview.outcome_counts ?? {}).map(([outcome, count]) => (
              <Badge key={outcome} variant="outline" className="text-[10px]">
                {outcome}: {count}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Diagnose-before-fixing (CLAUDE.md): for one recognized vendor x one
// entity, shows the exact join query, the invoice population it saw
// (broken down by source), how many matched, and the nearest-miss
// supplier names that didn't -- e.g. a compound brand name like
// "Emergentagent" against a bare panel entry "Emergent" -- so a "no
// invoices" report can be root-caused against real data instead of
// guessed. "Add as alias" closes the loop without leaving the page.
function FieldSamplePanel() {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [entityId, setEntityId] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [savingAlias, setSavingAlias] = useState(null);

  const loadVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const { data } = await subscriptionVendorAPI.list();
      const list = data?.vendors ?? [];
      setVendors(list);
      if (!vendorId && list.length > 0) setVendorId(list[0].id);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setVendorsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = () => {
    setOpen(true);
    if (vendors.length === 0) loadVendors();
  };

  const runSample = async () => {
    if (!vendorId || !entityId.trim()) return;
    setRunning(true);
    setError(null);
    try {
      const { data } = await subscriptionRebuildAPI.fieldSample(vendorId, entityId.trim());
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const addAsAlias = async (normalizedMiss) => {
    setSavingAlias(normalizedMiss);
    setError(null);
    try {
      const vendor = vendors.find((v) => v.id === vendorId);
      const nextAliases = Array.from(new Set([...(vendor?.aliases ?? []), normalizedMiss]));
      await subscriptionVendorAPI.setAliases(vendorId, nextAliases);
      await loadVendors();
      await runSample();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingAlias(null);
    }
  };

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={handleOpen} data-testid="subscription-rebuild-field-sample-open">
        <Search className="h-3 w-3 mr-1" />
        Field sample: why didn't a vendor convert?
      </Button>
    );
  }

  return (
    <div className="border rounded p-3 space-y-2 text-xs" data-testid="subscription-rebuild-field-sample">
      <p className="text-muted-foreground">
        Pick a recognized vendor and an entity to see the exact join query, the invoice
        population it found (by source), how many matched, and the nearest-miss supplier names
        that didn't -- e.g. a compound brand name a bare panel entry doesn't bridge on its own.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <select
          className="h-9 rounded-md border border-input bg-transparent px-2 text-xs"
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          disabled={vendorsLoading}
          data-testid="subscription-rebuild-field-sample-vendor"
        >
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.vendor_name}</option>
          ))}
        </select>
        <Input
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          placeholder="entity_id, e.g. bi_sas"
          className="h-9 w-40"
          data-testid="subscription-rebuild-field-sample-entity"
        />
        <Button
          size="sm" onClick={runSample} disabled={running || !vendorId || !entityId.trim()}
          data-testid="subscription-rebuild-field-sample-run"
        >
          {running ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Run
        </Button>
      </div>

      {error && <p className="text-red-600">{error}</p>}

      {result && !result.error && (
        <div className="space-y-2" data-testid="subscription-rebuild-field-sample-result">
          <div>
            <span className="font-medium">Query: </span>
            <code className="text-[10px]">{JSON.stringify(result.query)}</code>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-[10px]">Population: {result.population_total}</Badge>
            <Badge variant="outline" className="text-[10px]">Matched: {result.matched_count}</Badge>
            {Object.entries(result.population_by_source ?? {}).map(([source, count]) => (
              <Badge key={source} variant="outline" className="text-[10px]">
                {source}: {count}
              </Badge>
            ))}
          </div>
          {result.nearest_misses?.length > 0 ? (
            <table className="w-full text-[10px]" data-testid="subscription-rebuild-field-sample-misses">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pr-2">Invoice supplier name</th>
                  <th className="pr-2">Similarity</th>
                  <th className="pr-2">Source</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.nearest_misses.map((miss) => (
                  <tr key={miss.normalized} className="border-t">
                    <td className="pr-2 py-1">{miss.raw_supplier_client_name}</td>
                    <td className="pr-2 py-1">{Math.round(miss.similarity_to_vendor * 100)}%</td>
                    <td className="pr-2 py-1">{miss.source}</td>
                    <td className="py-1">
                      <Button
                        variant="outline" size="sm" className="h-6 text-[10px]"
                        onClick={() => addAsAlias(miss.normalized)}
                        disabled={savingAlias === miss.normalized}
                        data-testid={`subscription-rebuild-field-sample-add-alias-${miss.normalized}`}
                      >
                        {savingAlias === miss.normalized ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add as alias'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted-foreground">No near-miss invoice supplier names in the population.</p>
          )}
          {result.found_outside_entity_scope && (
            <FoundOutsideEntityScope data={result.found_outside_entity_scope} />
          )}
        </div>
      )}
      {result?.error && <p className="text-red-600" data-testid="subscription-rebuild-field-sample-error">{result.error}</p>}
    </div>
  );
}

// Automatic second pass: fires only when the entity-scoped first pass
// above found zero matches. Reruns the same vendor match unscoped by
// entity (every entity_id, including missing/unassigned) so a "no
// invoices" report can be root-caused as an entity-scope gap instead of
// a genuine mailbox-coverage gap (invoices never ingested at all).
function FoundOutsideEntityScope({ data }) {
  if (data.absent_from_store) {
    return (
      <div
        className="p-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900"
        data-testid="subscription-rebuild-field-sample-outside-scope"
      >
        <p className="font-medium">Found outside entity scope: none.</p>
        <p className="text-muted-foreground">
          This vendor has zero matching invoices anywhere in the store, for any entity. The gap is
          mailbox coverage (invoices never ingested), not entity/join scope.
        </p>
      </div>
    );
  }
  return (
    <div
      className="p-2 rounded border border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 space-y-1"
      data-testid="subscription-rebuild-field-sample-outside-scope"
    >
      <p className="font-medium">Found outside entity scope: {data.matched_count}</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.by_entity_id ?? {}).map(([eid, count]) => (
          <Badge key={eid} variant="outline" className="text-[10px]" data-testid={`subscription-rebuild-field-sample-outside-scope-entity-${eid}`}>
            {eid}: {count}
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.by_source ?? {}).map(([source, count]) => (
          <Badge key={source} variant="outline" className="text-[10px]">
            {source}: {count}
          </Badge>
        ))}
      </div>
    </div>
  );
}
